import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { Flag as FlagIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { AdminService } from '../../services/adminService';

/**
 * Jalons des comptes : premier combat d'onboarding, onboarding terminé, compte
 * identifié, compte payant. Pour chacun : combien l'ont franchi sur 30 jours, la
 * rétention à J+30, et l'évolution par semaine. Les définitions font foi côté
 * serveur (`admin.queries` → `JALONS_COMPTES_CTE`) ; cet écran ne recalcule rien.
 */

const COULEURS = {
  combat_1: '#2196f3',
  onboarding: '#4caf50',
  identifie: '#9c27b0',
  payant: '#ff9800',
};

// 'YYYY-MM-DD' lu à midi : un minuit UTC basculerait la veille selon le fuseau.
const formatJour = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR') : '—');
const formatSemaine = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
const formatTaux = (pct) => (pct === null || pct === undefined ? '—' : `${pct} %`);

function JalonCard({ jalon, fenetreJours, retention, payantsActifs }) {
  const { retention: r } = jalon;
  return (
    <Card sx={{ height: '100%', borderTop: `4px solid ${COULEURS[jalon.jalon]}` }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {jalon.label}
        </Typography>
        <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
          {jalon.atteintsFenetre}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          sur les {fenetreJours} derniers jours · {jalon.atteintsTotal} au total
        </Typography>
        {jalon.jalon === 'payant' && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Payants actifs aujourd'hui : <strong>{payantsActifs}</strong>
          </Typography>
        )}
        <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body1">
            Rétention J+{retention.jour} : <strong>{formatTaux(r.tauxPct)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {r.retenus} / {r.cohorte} comptes — jalon franchi du {formatJour(r.cohorteDebut)} au{' '}
            {formatJour(r.cohorteFin)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function JalonsComptesSection({ segment = 'familles' }) {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let annule = false;
    AdminService.getAccountMilestones(26, segment)
      .then((d) => { if (!annule) setDonnees(d); })
      .catch((err) => {
        console.error('Error fetching account milestones:', err);
        if (!annule) setErreur('Impossible de charger les jalons des comptes');
      })
      .finally(() => { if (!annule) setChargement(false); });
    return () => { annule = true; };
  }, [segment]);

  if (chargement) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (erreur || !donnees) {
    return <Alert severity="error">{erreur || 'Aucune donnée'}</Alert>;
  }

  const { jalons, retention, fenetreJours, tendance, payantsActifs } = donnees;
  const libelle = Object.fromEntries(jalons.map((j) => [j.jalon, j.label]));
  const derniereFenetre = retention.jour + retention.fenetre - 1;

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Jalons des comptes
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Tout joueur franchit les deux premiers jalons en <strong>invité anonyme</strong> : l'onboarding
        précède la création de compte. « Identifié » = rattaché à un parent, ou compte adulte authentifié.
        « Payant » = premier abonnement réellement payé (hors tests, cadeaux bêta et partage familial des
        stores) ; les comptes parents n'apparaissent que là. <strong>Rétention J+{retention.jour}</strong> =
        au moins une partie jouée entre J+{retention.jour} et J+{derniereFenetre} après le jalon (pour un
        payant parent, une partie de ses enfants). Avant la migration 157, la date de fin d'onboarding et
        celle de l'identification directe sont approchées (combat monstre, inscription).
      </Alert>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {jalons.map((j) => (
          <Grid item xs={12} md={6} lg={3} key={j.jalon}>
            <JalonCard jalon={j} fenetreJours={fenetreJours} retention={retention} payantsActifs={payantsActifs} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Jalons franchis par semaine"
              subheader="Comptes ayant franchi chaque jalon dans la semaine — et stock de payants actifs en fin de semaine"
              avatar={<FlagIcon color="primary" />}
            />
            <CardContent>
              <Box sx={{ height: 360, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tendance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="semaine" tickFormatter={formatSemaine} tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip labelFormatter={(s) => `Semaine du ${formatJour(s)}`} />
                    <Legend />
                    {jalons.map((j) => (
                      <Line
                        key={j.jalon}
                        type="monotone"
                        dataKey={j.jalon}
                        name={j.label}
                        stroke={COULEURS[j.jalon]}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                      />
                    ))}
                    <Line
                      type="stepAfter"
                      dataKey="payantsActifs"
                      name="Payants actifs (stock)"
                      stroke={COULEURS.payant}
                      strokeDasharray="6 4"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={`Rétention J+${retention.jour} par jalon`}
              subheader={`Par semaine où le jalon a été franchi, cohortes cumulées sur ${retention.lissageSemaines} semaines. Les ${Math.ceil(derniereFenetre / 7)} dernières semaines n'ont pas encore de recul.`}
              avatar={<TrendingUpIcon color="primary" />}
            />
            <CardContent>
              <Box sx={{ height: 360, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tendance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="semaine" tickFormatter={formatSemaine} tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} unit=" %" />
                    <RechartsTooltip
                      labelFormatter={(s) => `Semaine du ${formatJour(s)}`}
                      formatter={(valeur, nom, item) => {
                        const jalon = String(item.dataKey).replace('retention_', '');
                        const base = item.payload?.[`base_${jalon}`] ?? 0;
                        return [`${formatTaux(valeur)} (base ${base})`, nom];
                      }}
                    />
                    <Legend />
                    {jalons.map((j) => (
                      <Line
                        key={j.jalon}
                        type="monotone"
                        dataKey={`retention_${j.jalon}`}
                        name={libelle[j.jalon]}
                        stroke={COULEURS[j.jalon]}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
